import React, {useId, useRef} from "react";
import {C} from "../styles";
import useDialogBehavior from "./useDialogBehavior";
import useFloatingPanel from "./floatingPanel";

// Boîte de dialogue modale. Le comportement clavier / lecteur d'écran (Échap sur la boîte du dessus, focus piégé, reste de la
// page inerte, défilement bloqué, focus rendu au déclencheur) est dans useDialogBehavior, partagé avec la lightbox vidéo.
export default function Modal({floating = false, ...props}) {
    return floating ? <FloatingModal {...props}/> : <FixedModal {...props}/>;
}

// Poignées de redimensionnement : 4 bords et 4 coins. Elles restent à l'extérieur du cadre (jamais sur la barre de défilement
// du contenu). Décoratives pour la souris et le tactile ; le clavier passe par la barre de titre (flèches, Maj + flèches).
const HANDLES = {
    n: {top: -8, left: 12, right: 12, height: 8, cursor: "ns-resize"},
    s: {bottom: -8, left: 12, right: 12, height: 8, cursor: "ns-resize"},
    e: {right: -8, top: 12, bottom: 12, width: 8, cursor: "ew-resize"},
    w: {left: -8, top: 12, bottom: 12, width: 8, cursor: "ew-resize"},
    ne: {top: -8, right: -8, width: 16, height: 16, cursor: "nesw-resize"},
    nw: {top: -8, left: -8, width: 16, height: 16, cursor: "nwse-resize"},
    se: {bottom: -8, right: -8, width: 16, height: 16, cursor: "nwse-resize"},
    sw: {bottom: -8, left: -8, width: 16, height: 16, cursor: "nesw-resize"},
};

const HIDDEN_TEXT = {position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap"};
const KEYBOARD_HELP = "Flèches : déplacer la fenêtre. Maj et flèches : la redimensionner.";

// Variante flottante : on la déplace par la barre de titre et on la redimensionne par ses bords, comme une fenêtre Windows.
// Elle reste modale (focus piégé, page inerte : useDialogBehavior) mais le voile est léger pour garder l'aperçu visible, et un
// clic à côté ne la ferme pas (un redimensionnement relâché hors du cadre ferait perdre la saisie).
function FloatingModal({title, onClose, children, initialFocusRef}) {
    const titleId = useId();
    const panelRef = useRef(null);
    useDialogBehavior(panelRef, onClose, initialFocusRef);
    const {rect, handleProps, onKeyDown} = useFloatingPanel();
    const helpId = useId();

    return (
        <div style={{position: "fixed", inset: 0, backgroundColor: "rgba(36,57,56,0.18)", zIndex: 1000}}>
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                style={{
                    position: "fixed", left: rect.x, top: rect.y, width: rect.w, height: rect.h,
                    display: "flex", flexDirection: "column",
                    backgroundColor: C.surface, borderRadius: 14, boxShadow: "0 12px 40px rgba(0,0,0,0.25)", outline: "none",
                }}
            >
                <div
                    {...handleProps("move")}
                    onKeyDown={onKeyDown}
                    tabIndex={0}
                    aria-describedby={helpId}
                    aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight Shift+ArrowUp Shift+ArrowDown Shift+ArrowLeft Shift+ArrowRight"
                    title={`Glisser pour déplacer la fenêtre. ${KEYBOARD_HELP}`}
                    style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px 8px 24px", cursor: "move", userSelect: "none", touchAction: "none", borderBottom: `1px solid ${C.border}`}}
                >
                    <h3 id={titleId} style={{margin: 0, fontFamily: "'Fraunces', serif", color: C.primaryDark}}>{title}</h3>
                    <span id={helpId} style={HIDDEN_TEXT}>{KEYBOARD_HELP}</span>
                    <button onClick={onClose} aria-label="Fermer" style={{background: "none", border: "none", cursor: "pointer", fontSize: 18, color: C.textMuted, lineHeight: 1}}>
                        ✕
                    </button>
                </div>
                <div style={{flex: 1, minHeight: 0, overflowY: "auto", padding: "12px 24px 16px", marginBottom: 8}}>{children}</div>
                {Object.entries(HANDLES).map(([dir, style]) => (
                    <div key={dir} data-resize-handle={dir} aria-hidden="true" {...handleProps(dir)} style={{position: "absolute", touchAction: "none", ...style}}/>
                ))}
            </div>
        </div>
    );
}

function FixedModal({title, onClose, children, scrollable = false, initialFocusRef}) {
    const titleId = useId();
    const panelRef = useRef(null);
    useDialogBehavior(panelRef, onClose, initialFocusRef);

    return (
        <div
            onClick={onClose}
            style={{
                position: "fixed", inset: 0, backgroundColor: "rgba(36,57,56,0.45)",
                display: "flex", alignItems: "center", justifyContent: "center",
                zIndex: 1000, padding: 16,
            }}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                tabIndex={-1}
                onClick={e => e.stopPropagation()}
                style={{
                    backgroundColor: C.surface,
                    borderRadius: 14,
                    padding: 24,
                    maxWidth: 440,
                    width: "100%",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
                    outline: "none",
                    ...(scrollable ? {maxHeight: "80vh", overflowY: "auto"} : {}),
                }}
            >
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8}}>
                    <h3 id={titleId} style={{margin: 0, fontFamily: "'Fraunces', serif", color: C.primaryDark}}>
                        {title}
                    </h3>
                    <button onClick={onClose} aria-label="Fermer" style={{background: "none", border: "none", cursor: "pointer", fontSize: 18, color: C.textMuted, lineHeight: 1}}>
                        ✕
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}
